/// <reference types="google.maps" />

import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { ErrorMessage } from '@hookform/error-message';
import { log } from '@lib/logs';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCombobox } from 'downshift';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ErrorText } from './FormComponents';
import { InputLoadingSpinner } from './InputLoadingSpinner';

const CHARS_TO_DEBOUNCE = 5; // Number of characters to wait before fetching addresses

function AutocompleteInput({ ausOnly }: { ausOnly: boolean }) {
  const { watch, setValue, formState, register } = useFormContext();
  const [debouncedAddress, setDebouncedAddress] = useState<string>('');
  const [items, setItems] = useState<google.maps.places.PlacePrediction[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const placesLibrary = useRef<google.maps.PlacesLibrary | null>(null);

  register('addressAutocomplete');
  const addressValue = watch('addressAutocomplete');

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setValue('addressAutocomplete', inputValue, { shouldValidate: true }); // It is a controlled input, set the value for it to validate!
    },
    items,
    itemToString(item) {
      return item ? item.text.text : '';
    },
  });

  // Load in Autocomplete
  useEffect(() => {
    setOptions({
      key: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
      v: 'weekly',
    });

    const loadGoogleMaps = async () => {
      const lib = await importLibrary('places');
      placesLibrary.current = lib;
    };

    loadGoogleMaps();
  }, []);

  // Debounce the address input to avoid excessive API calls
  useEffect(() => {
    if (addressValue === selectedItem?.text.text) return; // this is after the address is selected to prevent the API call

    setShowLoader(true); // indicate search while debouncing
    const hideLoader = setTimeout(() => {
      setShowLoader(false); // stop loader after 5 seconds if no more typing
    }, 5000);

    const debounceHandler = setTimeout(() => {
      setDebouncedAddress(addressValue);
    }, 500);

    // Clear the timeout if the component unmounts or addressValue changes (i.e. continue typing)
    return () => {
      clearTimeout(debounceHandler);
      clearTimeout(hideLoader);
    };
  }, [addressValue, selectedItem]);

  // When the debounced address changes, fetch addresses
  useEffect(() => {
    if (debouncedAddress && debouncedAddress.length > CHARS_TO_DEBOUNCE) {
      // Fetch addresses based on the debounced address value
      placesLibrary.current?.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        {
          input: debouncedAddress,
          includedRegionCodes: ausOnly ? ['au'] : ['au', 'nz'],
          region: 'au',
          includedPrimaryTypes: ['street_address'],
        }
      ).then(({ suggestions }) => {
        setItems(
          suggestions.map((s) => s.placePrediction).filter((s) => s !== null)
        );
        setShowLoader(false);
      });
    } else {
      setItems([]); // Clear addresses if input is empty
    }
  }, [debouncedAddress, ausOnly]);

  // When the selected address changes, validate it
  useEffect(() => {
    if (selectedItem) {
      console.log('Selected address:', selectedItem);
      setShowLoader(false);
      // Get metadata for the selected address
      selectedItem
        .toPlace()
        .fetchFields({
          fields: ['addressComponents', 'formattedAddress'],
        })
        .then(({ place }) => {
          console.log(place);
          try {
            const unit = place?.addressComponents?.filter((c) =>
              c.types.includes('subpremise')
            )[0]?.longText;

            const streetNumber = place?.addressComponents?.filter((c) =>
              c.types.includes('street_number')
            )[0]?.shortText;

            const street = place?.addressComponents?.filter((c) =>
              c.types.includes('route')
            )[0]?.longText;

            setValue(
              'address1',
              `${unit ? `${unit}/` : ''}${streetNumber ? streetNumber : ''} ${street ? street : ''}`.trim(),
              { shouldValidate: true }
            );
            setValue(
              'postcode',
              place?.addressComponents?.filter((c) =>
                c.types.includes('postal_code')
              )[0]?.longText,
              { shouldValidate: true }
            );
            setValue(
              'suburb',
              place?.addressComponents?.filter((c) =>
                c.types.includes('locality')
              )[0]?.longText,
              { shouldValidate: true }
            );
            setValue(
              'state',
              place?.addressComponents?.filter((c) =>
                c.types.includes('administrative_area_level_1')
              )[0]?.longText,
              { shouldValidate: true }
            );
            setValue(
              'country',
              place?.addressComponents?.filter((c) =>
                c.types.includes('country')
              )[0]?.shortText,
              { shouldValidate: true }
            );
            setValue('addressAutocomplete', place.formattedAddress, {
              shouldValidate: true,
            });
          } catch (e) {
            log({ error: e });
          }
        });
    }
  }, [selectedItem, setValue]);

  return (
    <BulkErrorBoundary location="Google autocomplete">
      <div className="flex flex-col gap-1">
        <Controller
          name={'addressAutocomplete' as any}
          rules={{
            required: { value: true, message: 'This field is required' },
            validate: {
              validateAddress: (
                value,
                { address1, postcode, suburb, state, country }
              ) => {
                if (!address1 || !postcode || !suburb || !state || !country) {
                  return 'Please select an address from the dropdown';
                }
                return true;
              },
            },
            deps: ['address1', 'postcode', 'suburb', 'state', 'country'],
          }}
          render={({ field: { onChange, onBlur, value, name, ref } }) => (
            <div>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: <it comes from the spread of props> */}
              <label {...getLabelProps()} className="text-xs font-bold pl-2">
                Address
                <span className="ml-1 text-xs uppercase text-red">*</span>
                <VisuallyHidden> required</VisuallyHidden>
              </label>
              <div className="flex flex-col gap-1 relative">
                <input
                  data-1p-ignore // 1pass
                  data-lpignore="true" // lastpass
                  data-form-type="other" // dashlane
                  data-bwignore // bitwarden
                  placeholder="Start typing your address and select from the dropdown to proceed"
                  className="h-10 min-w-[200px] border border-solid border-grey-mid bg-white p-2.5 text-black placeholder:text-grey-dark rounded-full"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  {...getInputProps()}
                />
                <InputLoadingSpinner
                  active={showLoader && addressValue?.length > 0}
                />
              </div>
              <ul
                className={`w-72 max-h-80 absolute z-2 mt-1 overflow-y-scroll bg-white p-0 shadow-md dark:bg-grey-darkest ${!isOpen && 'hidden'}`}
                {...getMenuProps()}
              >
                {isOpen &&
                  items?.map((item, index) => (
                    <li
                      className={`
                  ${highlightedIndex === index && 'bg-orange text-white'}
                  ${selectedItem?.placeId === item.placeId && 'font-bold'}
                  flex flex-col px-3 py-2 shadow-xs
                `}
                      key={item.placeId}
                      {...getItemProps({ item, index })}
                    >
                      <span>{item.text.text}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        />
        {formState.errors.addressAutocomplete ? (
          <ErrorMessage
            errors={formState.errors}
            name="addressAutocomplete"
            render={({ message }) => <ErrorText message={message} />}
          />
        ) : null}
      </div>
    </BulkErrorBoundary>
  );
}

export function GoogleAutocomplete({ ausOnly }: { ausOnly: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <AutocompleteInput ausOnly={ausOnly} />
    </div>
  );
}
